import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ExploreButton from '../components/ExploreButton';
import SEO from '../components/SEO';
import HorizontalScrollText from '../components/HorizontalScrollText';
import TextReveal from '../components/TextReveal';
import DustTextReveal from '../components/DustTextReveal';
import PageWrapper from '../components/PageWrapper';
import BackgroundAnimation from '../components/BackgroundAnimation';
import HeroGlobe from '../components/HeroGlobe';
import api from '../services/api';
import { getMediaUrl } from '../utils/mediaUrl';
import ScrollStrokeBox from '../components/ScrollStrokeBox';
import '../styles/pages/HomePage.css';

gsap.registerPlugin(ScrollTrigger);

const HomePage = () => {
    const [email, setEmail] = useState('');
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mutedStates, setMutedStates] = useState({});
    const videoRefs = useRef({});
    const observerRef = useRef(null);
    const snapTriggersRef = useRef([]);


    useEffect(() => {
        const fetchSectionContents = async () => {
            try {
                const response = await api.getTabContents();
                if (response.success) {
                    const sectionsData = response.data.map((tab, index) => ({
                        ...tab,
                        position: index % 2 === 0 ? 'left' : 'right',
                        // ✅ Convert relative paths to full URLs
                        video: getMediaUrl(tab.video),
                        image: getMediaUrl(tab.image)
                    }));
                    setSections(sectionsData);

                    // Initialize muted states
                    const initialMutedStates = {};
                    sectionsData.forEach(section => {
                        initialMutedStates[section.tab_name] = true;
                    });
                    setMutedStates(initialMutedStates);
                }
            } catch (error) {
                console.error('Error fetching section contents:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchSectionContents();
    }, []);

    // GSAP scroll snap — desktop only (mobile uses native CSS scroll-snap)
    useEffect(() => {
        if (loading || sections.length === 0) return;
        if (window.innerWidth <= 768) return; // Mobile: let CSS snap handle it

        const timer = setTimeout(() => {
            const impactSections = document.querySelectorAll('.impact-card-section');
            snapTriggersRef.current = [];

            impactSections.forEach((el) => {
                const snapTo = () => {
                    window.scrollTo({
                        top: el.getBoundingClientRect().top + window.scrollY,
                        behavior: 'smooth',
                    });
                };

                const trigger = ScrollTrigger.create({
                    trigger: el,
                    start: 'top 60%',
                    end: 'bottom 40%',
                    onEnter: snapTo,
                    onEnterBack: snapTo,
                });
                snapTriggersRef.current.push(trigger);
            });
        }, 300);

        return () => {
            clearTimeout(timer);
            snapTriggersRef.current.forEach(t => t.kill());
            snapTriggersRef.current = [];
        };
    }, [loading, sections]);



    // ... rest of your code stays exactly the same ...
    // Intersection Observer for videos
    useEffect(() => {
        if (sections.length === 0) return;

        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const videoElement = entry.target;
                    if (entry.isIntersecting) {
                        videoElement.play().catch(err => {
                            console.log('Video autoplay failed:', err);
                        });
                    } else {
                        videoElement.pause();
                    }
                });
            },
            {
                threshold: 0.5,
                rootMargin: '0px'
            }
        );

        Object.values(videoRefs.current).forEach(video => {
            if (video) {
                observerRef.current.observe(video);
            }
        });

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [sections]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) {
            toast.error('Please enter your email');
            return;
        }
        if (!/^\S+@\S+$/i.test(email)) {
            toast.error('Invalid email address');
            return;
        }

        try {
            const response = await api.subscribeNewsletter(email);
            if (response.success) {
                toast.success(response.message || 'Successfully subscribed!');
                setEmail('');
            }
        } catch (error) {
            toast.error('Subscription failed. Please try again.');
        }
    };

    const toggleMute = (sectionName) => {
        const video = videoRefs.current[sectionName];
        if (video) {
            video.muted = !video.muted;
            setMutedStates(prev => ({
                ...prev,
                [sectionName]: video.muted
            }));
        }
    };

    const rewindVideo = (sectionName) => {
        const video = videoRefs.current[sectionName];
        if (video) {
            video.currentTime = Math.max(0, video.currentTime - 10);
        }
    };

    const forwardVideo = (sectionName) => {
        const video = videoRefs.current[sectionName];
        if (video) {
            video.currentTime = Math.min(video.duration, video.currentTime + 10);
        }
    };

    return (
        <PageWrapper>
            <SEO
                title="Supernova Lab | Barbados' National Engine for Public-Interest Experimentation"
                description="Building what you need next. A state-backed R&D and experimentation platform transforming how governments design, test and scale innovation."
                keywords="innovation, experimentation, R&D, Barbados, government innovation, public interest, policy lab"
                ogImage="https://supernovalab.bb/assets/images/og-home.jpg"
                canonicalUrl="https://supernovalab.bb"
            />


            <BackgroundAnimation />
            <main>
                {/* HERO SECTION */}
                <section className="hero-section" style={{ position: 'relative', overflow: 'hidden' }}>
                    <HeroGlobe />
                    <div className="container px-3 px-md-4" style={{ position: 'relative', zIndex: 1 }}>
                        <div className="row">
                            <div className="col-12">

                                {/* Tagline: Dust particle reveal, left to right */}
                                <div className="hero-small-text mb-3 mb-md-4">
                                    <DustTextReveal
                                        text="Driven by Science."
                                        delay={0.3}
                                        stagger={0.04}
                                        charDuration={0.6}
                                    />
                                    <br className="d-md-none" />
                                    <span className="d-none d-md-inline">&nbsp;</span>
                                    <DustTextReveal
                                        text="Fueled by Imagination."
                                        delay={1.2}
                                        stagger={0.035}
                                        charDuration={0.6}
                                    />
                                </div>

                                <h1 className="hero-title mb-4 mb-md-3">
                                    <TextReveal
                                        text="Designed"
                                        inline={true}
                                        delay={1.9}
                                        staggerChildren={0.12}
                                        once={true}
                                    />
                                    <br className="mobile-only-br" />
                                    {' '}
                                    <TextReveal
                                        text="for"
                                        inline={true}
                                        delay={2.1}
                                        staggerChildren={0.12}
                                        once={true}
                                    />
                                    {' '}
                                    <motion.span
                                        className="animated-title-primary"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 1.2, delay: 2.6 }}
                                    >
                                        Impact.
                                    </motion.span>
                                </h1>

                                <motion.div
                                    className="hero-description mb-4 mb-md-5"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 1, delay: 2.8 }}
                                >
                                    <p className="mb-1">
                                        <span className="mb-1">Supernova Lab is Barbados'</span>{' '}
                                        <span className="text-cyan">national engine</span>{' '}
                                        <span>for</span>{' '}
                                        <span>public-interest experimentation</span>
                                    </p>
                                    <p className="small-text text-muted mt-3">
                                        A mission-aligned proving ground for the systems of tomorrow.
                                    </p>
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, delay: 3.3 }}
                                >
                                    <ExploreButton to="/about-us">Explore</ExploreButton>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </section>

                <HorizontalScrollText />

                {/* IMPACT CARD SECTIONS */}
                {sections.map((section, index) => (
                    <section key={index} className="impact-card-section">
                        <div className="container-fluid px-3 px-md-4">
                            <div className="impact-card">
                                <div className="row g-0 mb-3">
                                    <div className="col-12">
                                        <h2 className="impact-card-title text-start">
                                            <TextReveal
                                                text={section.tab_name}
                                                staggerChildren={0.1}
                                                once={false}
                                            />
                                        </h2>
                                    </div>
                                </div>

                                <div className="row g-0 mb-4">
                                    <div className="col-12">
                                        <div className="d-flex justify-content-center">
                                            <div className="impact-card-content-wrapper">
                                                <div className="impact-card-text">
                                                    <TextReveal
                                                        text={section.text}
                                                        staggerChildren={0.02}
                                                        once={false}
                                                    />
                                                </div>
                                                <motion.div
                                                    initial={{ opacity: 0, y: 20 }}
                                                    whileInView={{ opacity: 1, y: 0 }}
                                                    viewport={{ once: false, margin: "-100px" }}
                                                    transition={{ duration: 0.5, delay: 0.2 }}
                                                >
                                                    <Link to={section.link}>
                                                        <button className="see-more-btn">Learn More</button>
                                                    </Link>
                                                </motion.div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <motion.div
                                    className="row g-0 impact-card-video-row"
                                    style={{ marginTop: '1.5rem' }}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: false, margin: "-100px" }}
                                    transition={{ duration: 0.8, delay: 0.2 }}
                                >
                                    <div className="col-12" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                                        <ScrollStrokeBox borderRadius={20}>
                                          <div className="impact-card-media">
                                            {section.video ? (
                                                <>
                                                    <video
                                                        ref={el => videoRefs.current[section.tab_name] = el}
                                                        loop
                                                        muted
                                                        playsInline
                                                        className="impact-card-video"
                                                        poster={section.image}
                                                    >
                                                        <source src={section.video} type="video/mp4" />
                                                    </video>

                                                    <div className="video-controls">
                                                        <button
                                                            className="video-control-btn"
                                                            onClick={() => rewindVideo(section.tab_name)}
                                                            aria-label="Rewind 10 seconds"
                                                        >
                                                            <i className="fas fa-rotate-left"></i>
                                                        </button>
                                                        <button
                                                            className="video-control-btn"
                                                            onClick={() => toggleMute(section.tab_name)}
                                                            aria-label={mutedStates[section.tab_name] ? "Unmute" : "Mute"}
                                                        >
                                                            <i className={`fas ${mutedStates[section.tab_name] ? 'fa-volume-mute' : 'fa-volume-up'}`}></i>
                                                        </button>
                                                        <button
                                                            className="video-control-btn"
                                                            onClick={() => forwardVideo(section.tab_name)}
                                                            aria-label="Forward 10 seconds"
                                                        >
                                                            <i className="fas fa-rotate-right"></i>
                                                        </button>
                                                    </div>
                                                </>
                                            ) : (
                                                <img
                                                    src={section.image}
                                                    alt={section.tab_name}
                                                    className="impact-card-video"
                                                />
                                            )}
                                          </div>
                                        </ScrollStrokeBox>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </section>
                ))}

                {/* NEWSLETTER SECTION */}
                <section className="newsletter-section">
                    <div className="container px-3 px-md-4">
                        <div className="row justify-content-center">
                            <div className="col-12 col-lg-10 text-center">
                                <h2 className="newsletter-title mb-0 mb-md-0">
                                    <DustTextReveal
                                        text="SUBSCRIBE"
                                        delay={0}
                                        stagger={0.06}
                                        charDuration={0.7}
                                        scrollTrigger={true}
                                    />
                                </h2>
                                <div className="newsletter-description px-2 px-md-0">
                                    <TextReveal
                                        text="Subscribe to our newsletter for the latest projects, trends, and exclusive content."
                                        staggerChildren={0.02}
                                        once={false}
                                    />
                                </div>
                                <div className='mb-3 mb-md-5'>
                                    <span className='px-1 newsletter-color'>
                                        No spam.
                                    </span>
                                    <span>
                                        Always engaging.
                                    </span>
                                </div>
                                <motion.form
                                    onSubmit={handleSubmit}
                                    className="newsletter-form-alt px-2 px-md-0"
                                    style={{ width: '100%' }}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: false, margin: "-100px" }}
                                    transition={{ duration: 0.6, delay: 0.3 }}
                                >
                                    <input
                                        type="email"
                                        className="newsletter-input-alt"
                                        placeholder="Enter your email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                    <button className="newsletter-btn-alt" type="submit">
                                        Subscribe
                                    </button>
                                </motion.form>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </PageWrapper>
    );
};

export default HomePage;